import {
    coalesceExecutionStrategies,
    findMatchingTestStep,
    mergeExecutionStrategies,
} from './executionStrategiesUtils';
import { getRecordingStepPassed } from './recordingPlanMarkers';

function resolveStepStatus(action, matchingTestStep) {
    if (action.details?.error) return 'failed';
    if (action.result?.success === false) return 'failed';
    if (action.result?.success === true) return 'success';
    if (action.details?.passed === true) return 'success';
    if (action.details?.passed === false) return 'failed';
    if (matchingTestStep?.status) return matchingTestStep.status;
    return 'pending';
}

function resolveVerificationStatus(relatedAction, relatedTestStep) {
    const passedArg = relatedAction.details?.arguments?.find((arg) => arg.name === 'passed');
    const nestedEvalPassed =
        relatedAction.result?.result?.passed ??
        relatedAction.result?.passed ??
        relatedTestStep?.result?.result?.passed ??
        relatedTestStep?.result?.passed;
    const argsPassed = relatedAction.args?.passed ?? relatedAction.arguments?.passed;
    const passedValue =
        passedArg?.raw ??
        argsPassed ??
        relatedAction.details?.passed ??
        relatedTestStep?.passed ??
        nestedEvalPassed;
    if (relatedAction.details?.error) return 'failed';
    if (passedValue === true || passedValue === 'true') return 'success';
    if (passedValue === false || passedValue === 'false') return 'failed';
    const mergedPassed = getRecordingStepPassed({
        ...relatedAction,
        passed: relatedTestStep?.passed,
        args: relatedAction.args || relatedAction.arguments,
    });
    if (mergedPassed === true) return 'success';
    if (mergedPassed === false) return 'failed';
    return 'pending';
}

function screenshotFromMap(screenshotsMap, executionId, fallbackAction) {
    const screenshotObj = screenshotsMap[executionId];
    return (
        screenshotObj?.url ??
        screenshotObj?.screenshotValue ??
        screenshotObj?.screenshot ??
        fallbackAction?.screenshotPath ??
        fallbackAction?.details?.screenshotPath ??
        null
    );
}

function buildExistingStepsByExecutionId(instructions = []) {
    const map = new Map();
    instructions.forEach((inst) => {
        (inst.aiSteps ?? []).forEach((step) => {
            if (step?.executionId != null) {
                map.set(String(step.executionId), step);
            }
        });
    });
    return map;
}

/**
 * Merge live generation executionFlow into instruction rows (streaming preview).
 */
export function mergeExecutionFlowIntoInstructions(
    instructions = [],
    executionFlow,
    screenshots = [],
    testStepsByExecutionId = {}
) {
    if (!executionFlow?.executions || !Array.isArray(executionFlow.executions)) {
        return instructions;
    }

    const screenshotsMap = {};
    for (const shot of screenshots ?? []) {
        if (shot?.executionId) screenshotsMap[shot.executionId] = shot;
    }

    const existingByExecutionId = buildExistingStepsByExecutionId(instructions);
    const executionsByInstructionId = {};
    let flatStepIndex = 0;

    for (const executionGroup of executionFlow.executions) {
        const mainAction = executionGroup.mainAction;
        const instructionId = mainAction?.instructionId ?? mainAction?.args?.instructionId;
        const instructionKey = instructionId != null ? String(instructionId) : null;
        if (!instructionKey) continue;

        if (!executionsByInstructionId[instructionKey]) {
            executionsByInstructionId[instructionKey] = [];
        }

        const matchingTestStep = findMatchingTestStep(
            testStepsByExecutionId,
            executionGroup,
            mainAction,
            flatStepIndex
        );
        const previousStep = existingByExecutionId.get(String(executionGroup.id));
        const executionStrategies = mergeExecutionStrategies(
            coalesceExecutionStrategies(matchingTestStep, mainAction),
            previousStep?.executionStrategies
        );

        const filteredArgs = { ...(mainAction.args ?? {}) };
        delete filteredArgs.instructionId;
        delete filteredArgs.ref;

        const screenshotValue = screenshotFromMap(screenshotsMap, executionGroup.id, mainAction);

        executionsByInstructionId[instructionKey].push({
            ...mainAction,
            id: `${executionGroup.id}-main`,
            executionId: executionGroup.id,
            description: mainAction.details?.description || mainAction.tool,
            tool: mainAction.tool,
            confidence: matchingTestStep?.confidence ?? mainAction.details?.confidence,
            stepType: 'ai_generated',
            arguments: filteredArgs,
            args: filteredArgs,
            status: resolveStepStatus(mainAction, matchingTestStep),
            instructionId,
            details: mainAction.details,
            screenshot: screenshotValue,
            screenshotPath: screenshotValue,
            duration: mainAction.details?.duration,
            executionGroup,
            error: mainAction.details?.error,
            ...(executionStrategies ? { executionStrategies } : {}),
        });
        flatStepIndex += 1;

        for (const [relatedIndex, relatedAction] of (executionGroup.related ?? []).entries()) {
            const relatedExecutionId = relatedAction.id ?? `${executionGroup.id}-related-${relatedIndex}`;
            const relatedTestStep = findMatchingTestStep(
                testStepsByExecutionId,
                { id: relatedExecutionId },
                relatedAction,
                flatStepIndex
            );
            const previousRelated = existingByExecutionId.get(String(relatedExecutionId));
            const relatedStrategies = mergeExecutionStrategies(
                coalesceExecutionStrategies(relatedTestStep, relatedAction),
                previousRelated?.executionStrategies
            );
            const relatedScreenshot = screenshotFromMap(screenshotsMap, relatedAction.id, relatedAction);

            executionsByInstructionId[instructionKey].push({
                ...relatedAction,
                id: `${executionGroup.id}-related-${relatedIndex}`,
                executionId: relatedExecutionId,
                description:
                    relatedTestStep?.stepDescription ||
                    relatedAction.details?.description ||
                    relatedAction.tool,
                tool: relatedAction.tool,
                confidence: relatedTestStep?.confidence ?? relatedAction.details?.confidence,
                stepType: 'verification',
                arguments: relatedAction.args || {},
                args: relatedAction.args || {},
                status: resolveVerificationStatus(relatedAction, relatedTestStep),
                instructionId,
                details: relatedAction.details,
                screenshot: relatedScreenshot,
                screenshotPath: relatedScreenshot,
                duration: relatedTestStep?.duration || relatedAction.details?.duration,
                executionGroup,
                error: relatedAction.details?.error,
                actualOutcome:
                    relatedTestStep?.actualOutcome ??
                    relatedAction.details?.outcome ??
                    relatedAction.details?.actualOutcome,
                reason:
                    relatedTestStep?.reason ??
                    relatedAction.details?.reason ??
                    relatedAction.reason,
                resultMessage:
                    relatedTestStep?.reason ??
                    relatedAction.details?.reason ??
                    relatedAction.reason,
                passed:
                    relatedTestStep?.passed ??
                    relatedAction.details?.passed,
                ...(relatedStrategies ? { executionStrategies: relatedStrategies } : {}),
            });
            flatStepIndex += 1;
        }
    }

    let globalIdx = 0;
    return instructions.map((instruction) => {
        const instructionKey = String(instruction.id);
        const stepsForInstruction = executionsByInstructionId[instructionKey] || [];
        if (stepsForInstruction.length === 0) return instruction;
        return {
            ...instruction,
            status: 'complete',
            aiSteps: stepsForInstruction.map((s) => ({ ...s, stepIndex: globalIdx++ })),
        };
    });
}
